// client/src/coms/views/canvas_parts/CanvasLeftToolRail.js
import React, { useEffect, useState } from 'react';
import { getReadyPaintDevice, subscribePaintDevice } from '../../canvas/paint-engine/integration/physicalPaintFlag';

const CANVAS_ICON_BASE = `${process.env.PUBLIC_URL || ''}/assets/canvas/icons`;
const LAST_SHAPE_TOOL_KEY = 'voxemera_canvas_last_shape_tool';
const LAST_SELECTION_TOOL_KEY = 'voxemera_canvas_last_selection_tool';
const SHAPE_TOOLS = new Set(['line', 'rectangle', 'ellipse']);
const SELECTION_TOOLS = new Set(['marquee_rect', 'marquee_ellipse', 'marquee_polygon', 'marquee_polygon_edit']);
const HIDDEN_3D_TOOL_IDS = new Set(['paint', 'calligraphy', 'jitter_blob', 'liquify', 'shape', 'pan']);
// The Paint tool also works on 3D models when its physical (WebGPU) engine runs.
const PHYSICAL_3D_TOOL_IDS = new Set(['paint']);

function usePhysicalPaintReady() {
  const [ready, setReady] = useState(() => Boolean(getReadyPaintDevice()));
  useEffect(() => subscribePaintDevice((device) => setReady(Boolean(device))), []);
  return ready;
}

const TOOL_GROUPS = [
  {
    id: 'brushes',
    tools: [
      { id: 'pen', label: 'Pen Brush', icon: '✎', iconPng: 'brush.png', tool: 'pen', preset: 'pen', clearsImportedBrush: true },
      { id: 'paint', label: 'Paint', icon: '🖌', tool: 'paint', clearsImportedBrush: true },
      { id: 'pencil', label: 'Pencil', icon: '✐', iconPng: 'pencil.png', tool: 'pen', preset: 'pencil', clearsImportedBrush: true },
      { id: 'calligraphy', label: 'Calligraphy', icon: '༄', iconPng: 'calligraphy.png', tool: 'pen', preset: 'calligraphy', clearsImportedBrush: true },
      { id: 'airbrush', label: 'Airbrush', icon: '⋰', iconPng: 'airbrush.png', tool: 'pen', preset: 'airbrush', clearsImportedBrush: true },
      { id: 'jitter_blob', label: 'Jitter Blob', icon: '✣', tool: 'pen', preset: 'jitter_blob', clearsImportedBrush: true },
      { id: 'liquify', label: 'Distortion / Liquify', icon: '⟲', tool: 'liquify', clearsImportedBrush: true },
    ],
  },
  {
    id: 'paint',
    tools: [
      { id: 'eraser', label: 'Eraser', icon: '⌫', iconPng: 'eraser.png', tool: 'eraser' },
      { id: 'bucket', label: 'Fill', icon: '▨', iconPng: 'bucket.png', tool: 'bucket', clearsImportedBrush: true },
      { id: 'text', label: 'Text', icon: 'T', tool: 'text', clearsImportedBrush: true },
    ],
  },
  {
    id: 'shapes',
    tools: [
      { id: 'shape', label: 'Shape', icon: '▱', tool: 'shape' },
    ],
  },
  {
    id: 'selection',
    tools: [
      { id: 'selection', label: 'Select', icon: '⬚', tool: 'selection', clearsImportedBrush: true },
    ],
  },
  {
    id: 'view',
    tools: [
      { id: 'move', label: 'Transform', icon: '⛶', tool: 'move', clearsImportedBrush: true },
      { id: 'pan', label: 'Pan', icon: '✋', tool: 'grab', clearsImportedBrush: true },
    ],
  },
];

function ToolIcon({ icon, iconPng, label }) {
  const [failed, setFailed] = useState(false);
  const src = iconPng ? `${CANVAS_ICON_BASE}/${iconPng}` : '';

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        onError={() => setFailed(true)}
        style={{
          width: 28,
          height: 28,
          objectFit: 'contain',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
    );
  }

  return <span aria-hidden="true">{icon || label}</span>;
}

function RailButton({ active, label, icon, iconPng, onClick }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        width: 42,
        height: 38,
        borderRadius: 4,
        border: active ? '1px solid rgba(180,215,255,0.56)' : '1px solid rgba(255,255,255,0.08)',
        background: active ? 'rgba(88,128,168,0.82)' : 'rgba(255,255,255,0.045)',
        color: 'rgba(245,247,250,0.96)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        fontWeight: 900,
        cursor: 'pointer',
        boxShadow: active ? 'inset 0 0 0 1px rgba(255,255,255,0.13)' : 'none',
        padding: 0,
        lineHeight: 1,
        flex: '0 0 auto',
      }}
    >
      <ToolIcon icon={icon} iconPng={iconPng} label={label} />
    </button>
  );
}

export default function CanvasLeftToolRail({
  activeTool,
  activeBrushPreset,
  activeImportedBrushId,
  setActiveTool,
  setActiveBrushPreset,
  setActiveImportedBrushId,
  onOpenFilters,
  replayPanelOpen,
  onToggleReplayPanel,
  canvasTabsVisible = true,
  onToggleCanvasTabs,
  modelViewOpen = false,
  // Drawing games pass their rules: tools they do not allow are left off the rail.
  isToolAllowed = null,
}) {
  const physicalPaintReady = usePhysicalPaintReady();
  const [lastShapeTool, setLastShapeTool] = useState(() => {
    if (SHAPE_TOOLS.has(activeTool)) return activeTool;
    if (typeof window === 'undefined') return 'line';
    try {
      const stored = window.localStorage.getItem(LAST_SHAPE_TOOL_KEY);
      return SHAPE_TOOLS.has(stored) ? stored : 'line';
    } catch {
      return 'line';
    }
  });

  const [lastSelectionTool, setLastSelectionTool] = useState(() => {
    if (SELECTION_TOOLS.has(activeTool)) return activeTool;
    if (typeof window === 'undefined') return 'marquee_rect';
    try {
      const stored = window.localStorage.getItem(LAST_SELECTION_TOOL_KEY);
      return SELECTION_TOOLS.has(stored) ? stored : 'marquee_rect';
    } catch {
      return 'marquee_rect';
    }
  });

  useEffect(() => {
    if (!SHAPE_TOOLS.has(activeTool)) return;
    setLastShapeTool(activeTool);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LAST_SHAPE_TOOL_KEY, activeTool);
    } catch {}
  }, [activeTool]);


  useEffect(() => {
    if (!SELECTION_TOOLS.has(activeTool)) return;
    setLastSelectionTool(activeTool);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LAST_SELECTION_TOOL_KEY, activeTool);
    } catch {}
  }, [activeTool]);

  function isActive(button) {
    if (button.filter) return false;
    if (button.tool === 'shape') return SHAPE_TOOLS.has(activeTool);
    if (button.tool === 'selection') return SELECTION_TOOLS.has(activeTool);
    if (button.tool === 'pen') {
      return activeTool === 'pen'
        && button.preset === activeBrushPreset
        && (!button.clearsImportedBrush || !activeImportedBrushId);
    }
    return activeTool === button.tool;
  }

  const visibleToolGroups = TOOL_GROUPS
    .map((group) => ({
      ...group,
      tools: (modelViewOpen
        ? group.tools.filter((button) => !HIDDEN_3D_TOOL_IDS.has(button.id) || (physicalPaintReady && PHYSICAL_3D_TOOL_IDS.has(button.id)))
        : group.tools
      ).map((button) => (
        modelViewOpen && button.id === 'move'
          ? {
              ...button,
              label: 'Transform Layer',
              icon: '⤢',
            }
          : button
      )),
    }))
    .map((group) => (typeof isToolAllowed === 'function'
      ? { ...group, tools: group.tools.filter((button) => (button.tool === 'shape' ? Array.from(SHAPE_TOOLS).some(isToolAllowed) : isToolAllowed(button.tool))) }
      : group))
    .filter((group) => group.tools.length > 0);

  const allowedShapeTool = typeof isToolAllowed === 'function' && !isToolAllowed(lastShapeTool)
    ? Array.from(SHAPE_TOOLS).find(isToolAllowed) || lastShapeTool
    : lastShapeTool;

  function selectButton(button) {
    if (button.filter) {
      if (typeof onOpenFilters === 'function') onOpenFilters();
      return;
    }

    if (button.clearsImportedBrush && typeof setActiveImportedBrushId === 'function') {
      setActiveImportedBrushId('');
    }

    if (typeof setActiveTool === 'function') {
      setActiveTool(
        button.tool === 'shape'
          ? allowedShapeTool
          : button.tool === 'selection'
            ? lastSelectionTool
            : button.tool
      );
    }
    if (button.preset && typeof setActiveBrushPreset === 'function') setActiveBrushPreset(button.preset);
  }

  return (
    <aside
      aria-label="Canvas tools"
      style={{
        width: 54,
        flex: '0 0 54px',
        borderRight: '1px solid rgba(0,0,0,0.50)',
        background: 'linear-gradient(to right, #3e3e3e, #363636)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        padding: '7px 5px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
          width: '100%',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {visibleToolGroups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            {groupIndex > 0 ? <div style={{ width: 34, height: 1, background: 'rgba(255,255,255,0.12)', margin: '2px 0' }} /> : null}
            {group.tools.map((button) => (
              <RailButton
                key={button.id}
                active={isActive(button)}
                label={button.label}
                icon={button.icon}
                iconPng={button.iconPng}
                onClick={() => selectButton(button)}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 5,
          flex: '0 0 auto',
          marginTop: 'auto',
          paddingTop: 6,
        }}
      >
        <div style={{ width: 34, height: 1, background: 'rgba(255,255,255,0.12)', margin: '2px 0' }} />
        <RailButton
          active={!!replayPanelOpen}
          label={replayPanelOpen ? 'Close drawing playback' : 'Play drawing'}
          icon="▶"
          onClick={() => {
            if (typeof onToggleReplayPanel === 'function') onToggleReplayPanel();
          }}
        />
        {typeof onToggleCanvasTabs === 'function' ? (
          <RailButton
            active={!canvasTabsVisible}
            label={canvasTabsVisible ? 'Hide canvas tabs' : 'Show canvas tabs'}
            icon={canvasTabsVisible ? '▼' : '▲'}
            onClick={() => onToggleCanvasTabs()}
          />
        ) : null}
      </div>
    </aside>
  );
}
